
'use client';

import { motion } from 'framer-motion';
import { useCallback } from 'react';

export default function HeroSection() {
  /**
   * Scrolls smoothly to the element with the given id.
   * Returns `true` if the element was found and scrolled to, otherwise `false`.
   */
  const smoothScrollTo = useCallback((id: string): boolean => {
    const element = document.getElementById(id);
    if (!element) {
      console.warn(`HeroSection: Unable to find element with id="${id}"`);
      return false;
    }
    try {
      element.scrollIntoView({ behavior: 'smooth' });
      return true;
    } catch (e) {
      console.error(`HeroSection: scrollIntoView failed for id="${id}"`, e);
      return false;
    }
  }, []);

  const scrollToOffer = useCallback(() => smoothScrollTo('offer'), [smoothScrollTo]);

  return (
    <section id="hero" className="relative min-h-[100dvh] w-full overflow-hidden bg-black">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/67c7380e546ced1bc700efebbb798a08.jpg"
          alt="Premium Pistol Lighter Background"
          className="w-full h-full object-cover opacity-40"
          style={{ filter: 'brightness(0.7)' }}
        />
        <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24 md:h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[100dvh] flex items-center justify-center pt-16 pb-12 px-4 sm:pt-20 sm:pb-16 md:pt-32 md:pb-24">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="font-serif text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-4 sm:mb-5 md:mb-6 leading-tight px-2">
              The Last Lighter You'll Ever Need
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto px-4 leading-relaxed"
          >
            Military-grade rechargeable pistol lighter + cigarette case that never lets you down. No more dead batteries, broken wheels, or cheap plastic garbage.
          </motion.p>

          {/* 3 Bullets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col gap-3 sm:gap-4 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto px-4"
          >
            <div className="flex items-start gap-3 text-left">
              <span className="text-xl sm:text-2xl flex-shrink-0">⚡</span>
              <div className="text-white/90 text-xs sm:text-sm md:text-base">
                <strong className="text-white">USB Rechargeable</strong> — Never buy fuel or batteries again
              </div>
            </div>
            <div className="flex items-start gap-3 text-left">
              <span className="text-xl sm:text-2xl flex-shrink-0">🛡️</span>
              <div className="text-white/90 text-xs sm:text-sm md:text-base">
                <strong className="text-white">Tactical Aluminum Build</strong> — Survives drops, water, and daily abuse
              </div>
            </div>
            <div className="flex items-start gap-3 text-left">
              <span className="text-xl sm:text-2xl flex-shrink-0">📦</span>
              <div className="text-white/90 text-xs sm:text-sm md:text-base">
                <strong className="text-white">Built-in Cigarette Case</strong> — Keeps your smokes fresh and protected
              </div>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mb-8 sm:mb-10 md:mb-12 px-4"
          >
            <motion.a
              href="#offer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-white text-black px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-full text-sm sm:text-base md:text-lg font-medium hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer active:scale-[0.98] shadow-xl"
            >
              BUY NOW - $49.90
            </motion.a>
          </motion.div>

          {/* Trust Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 md:gap-6 text-white/80 text-[10px] sm:text-xs md:text-sm px-4"
          >
            <div className="flex items-center gap-1.5">
              <i className="ri-truck-line text-sm sm:text-base md:text-lg" />
              <span>Free US Shipping</span>
            </div>
            <span className="text-white/40 hidden sm:inline">•</span>
            <div className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-sm sm:text-base md:text-lg" />
              <span>30-Day Guarantee</span>
            </div>
            <span className="text-white/40 hidden sm:inline">•</span>
            <div className="flex items-center gap-1.5">
              <i className="ri-lock-line text-sm sm:text-base md:text-lg" />
              <span>Secure Checkout</span>
            </div>
            <span className="text-white/40 hidden sm:inline">•</span>
            <div className="flex items-center gap-1.5">
              <i className="ri-customer-service-line text-sm sm:text-base md:text-lg" />
              <span>24/7 Support</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-5 h-8 md:w-6 md:h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-1.5 md:p-2"
        >
          <motion.div className="w-1 h-1.5 md:h-2 bg-white/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}
